import React, { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { KPIResult, Variable, TargetType, VariableType } from '@/lib/calculator';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

interface ResultsGridProps {
  results: KPIResult[][];
  rowVariables: Variable[];
  columnVariables: Variable[];
  target: TargetType;
  selectedCell: { row: number; col: number } | null;
  onCellSelect: (cell: { row: number; col: number } | null) => void;
  onStepEdit: (varType: VariableType) => void;
}

export function ResultsGrid({ 
  results, 
  rowVariables, 
  columnVariables, 
  target,
  selectedCell,
  onCellSelect
}: ResultsGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const selectedCellRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (
      results.length > 0 && 
      results[0].length > 0 && 
      !selectedCell
    ) {
      const middleRow = Math.floor(results.length / 2);
      const middleCol = Math.floor(results[0].length / 2);
      onCellSelect({ row: middleRow, col: middleCol });
    }
  }, [results.length, results[0]?.length, onCellSelect]);

  useEffect(() => {
    if (selectedCellRef.current && gridRef.current) {
      const grid = gridRef.current;
      const cell = selectedCellRef.current;
      
      const gridRect = grid.getBoundingClientRect();
      const cellRect = cell.getBoundingClientRect();
      
      const scrollLeft = cellRect.left - gridRect.left - (gridRect.width / 2) + (cellRect.width / 2);
      
      grid.scrollTo({
        left: Math.max(0, grid.scrollLeft + scrollLeft),
        behavior: 'smooth'
      });
    }
  }, [selectedCell]);

  if (rowVariables.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12 mb-16">
        <p className="text-lg text-gray-900 font-medium">No results to show yet</p>
        <p className="text-gray-600">Add inputs above to see your results here</p>
      </div>
    );
  }

  const formatDate = (date: Date | number): string => {
    return format(new Date(date), 'MMM d, yyyy');
  };

  // Calculate step based on index and direction
  const calculateStepValue = (variable: Variable, index: number): number => {
    const stepDirection = variable.stepDirection || 'both';
    let stepMultiplier = 0;
    
    if (stepDirection === 'both') {
      // Standard step calculation: -1, 0, 1
      stepMultiplier = index - 1;
    } else if (stepDirection === 'up') {
      // For 'up' direction: 0, 1, 2
      stepMultiplier = index;
    } else if (stepDirection === 'down') {
      // For 'down' direction: -2, -1, 0
      stepMultiplier = index - 2;
    }
    
    // Calculate value with step multiplier
    const valueAtStep = variable.value + (stepMultiplier * variable.step);
    
    // Ensure value is not negative
    return Math.max(0, valueAtStep);
  };

  const formatGridValue = (variable: Variable, index: number): string => {
    // Get value with step direction applied
    const valueAtStep = calculateStepValue(variable, index);
    
    // Format based on type
    switch (variable.type) {
      case 'price':
        return `Price: ${formatCurrency(valueAtStep)}`;
      case 'income':
        return `Income: ${formatCurrency(valueAtStep)}`;
      case 'savings':
        return `Monthly Savings: ${formatCurrency(valueAtStep)}`;
      case 'downPayment':
        return `Down Payment: ${formatCurrency(valueAtStep)}`;
      case 'interest':
        return `Mortgage Rate: ${formatNumber(valueAtStep)}%`;
      case 'date':
        return `Date: ${formatDate(valueAtStep)}`;
      default:
        return `${variable.label}: ${formatNumber(valueAtStep)}`;
    }
  };

  const getCellValue = (result: KPIResult): string => {
    if (!result) return 'N/A';
    
    switch (target) {
      case 'payment':
        return result.monthlyPayment ? formatCurrency(result.monthlyPayment) + '/mo' : 'Not possible';
      case 'savings':
        return result.requiredSavings === Infinity ? 'Not possible' : formatCurrency(result.requiredSavings) + '/mo';
      case 'date':
        return result.purchaseDate ? formatDate(result.purchaseDate) : 'Not possible';
      default:
        return formatCurrency(result.maxPrice);
    }
  };

  if (!results.length || !results[0].length) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12 mb-16">
        <p className="text-lg text-gray-900 font-medium">No results to show yet</p>
        <p className="text-gray-600">Add inputs above to see your results here</p>
      </div>
    );
  }

  const isArray = columnVariables.length === 0;

  return (
    <div className="space-y-6">
      <div className="overflow-x-auto">
        {isArray ? (
          // Array view (one column)
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {results.map((row, rowIndex) => {
              // Generate row values with direction consideration
              const rowValues = rowVariables.map(variable => ({
                label: formatGridValue(variable, rowIndex)
              }));

              const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === 0;
              
              return (
                <button
                  key={`row-${rowIndex}`}
                  ref={isSelected ? selectedCellRef : null}
                  onClick={() => onCellSelect({ row: rowIndex, col: 0 })}
                  className={`flex items-center gap-4 p-4 rounded-lg transition-all duration-200 text-left ${
                    isSelected 
                      ? 'bg-primary text-white border-2 border-primary shadow-md' 
                      : 'bg-white text-gray-900 border-2 border-primary border-opacity-50 hover:bg-gray-100'
                  }`}>
                  <div className="flex-1">
                    {rowValues.map((value, index) => (
                      <div 
                        key={`row-value-${rowIndex}-${index}`}
                        className={cn(
                          'text-sm whitespace-pre-line',
                          isSelected ? 'text-white' : 'text-gray-600'
                        )}>
                        {value.label}
                      </div>
                    ))}
                  </div>
                  <div className={cn(
                    'text-lg font-bold',
                    isSelected ? 'text-white' : 'text-gray-900'
                  )}>
                    {getCellValue(row[0])}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          // Grid mode - with column and row variables
          <div className="overflow-x-auto overscroll-x-contain -webkit-overflow-scrolling-touch" ref={gridRef}>
            <div className="min-w-max">
              <div 
                className="grid gap-[1px] bg-gray-200" 
                style={{ 
                  gridTemplateColumns: `auto repeat(${results[0].length}, auto)`,
                }}>
                {/* Empty top-left corner cell */}
                <div className="bg-gray-50 p-4 whitespace-pre-line" />
                
                {/* Column headers */}
                {results[0].map((_, colIndex) => {
                  const colValues = columnVariables.map(variable => ({
                    label: formatGridValue(variable, colIndex)
                  }));

                  return (
                    <div 
                      key={`header-col-${colIndex}`}
                      className="bg-gray-50 p-4 whitespace-pre-line">
                      {colValues.map((value, index) => (
                        <div key={`col-value-${colIndex}-${index}`} className="text-sm text-gray-600">
                          {value.label}
                        </div>
                      ))}
                    </div>
                  );
                })}

                {/* Rows with data */}
                {results.map((row, rowIndex) => (
                  <React.Fragment key={`row-${rowIndex}`}>
                    {/* Row header */}
                    <div className="bg-gray-50 p-4 whitespace-pre-line">
                      {rowVariables.map((variable, index) => (
                        <div key={`row-value-${rowIndex}-${index}`} className="text-sm text-gray-600">
                          {formatGridValue(variable, rowIndex)}
                        </div>
                      ))}
                    </div>
                    
                    {/* Row cells */}
                    {row.map((cell, colIndex) => {
                      const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
                      return (
                        <button
                          key={`cell-${rowIndex}-${colIndex}`}
                          ref={isSelected ? selectedCellRef : null}
                          className={`p-4 text-center whitespace-nowrap relative transition-all duration-200 ${
                            isSelected 
                              ? 'bg-primary text-white border-2 border-primary shadow-md' 
                              : 'bg-white text-gray-900 border-2 border-primary border-opacity-50 hover:bg-gray-100'
                          }`}
                          onClick={() => onCellSelect({ row: rowIndex, col: colIndex })}>
                          <div className={`text-lg font-bold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                            {getCellValue(cell)}
                          </div>
                        </button>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Remove the separator line */}
      {/* <div className="border-t border-gray-200/35 mt-4 pt-2"></div> */}
    </div>
  );
}